"use client";

import { Lock } from "lucide-react";
import {
  cancelFrame,
  type FrameData,
  frame,
  type MotionValue,
  motion,
  useMotionValue,
  useTransform,
} from "motion/react";
import {
  type PointerEvent as ReactPointerEvent,
  type RefObject,
  useCallback,
  useEffect,
  useMemo,
  useRef,
} from "react";
import { useLowPowerMode } from "@/hooks/use-low-power-mode";
import { uiHaptics } from "@/lib/ui-haptics";
import { cn } from "@/lib/utils";
import { StickerBlob, type StickerBlobLive } from "./sticker-blob";
import {
  stickerRimAngle,
  stickerSerial,
  stickerTone,
  withAlpha,
} from "./sticker-display";
import {
  approach,
  INPUT_TILT,
  idlePose,
  layersForPose,
  type SpringState,
  STATIC_POSE,
  type StickerLayers,
  stepSpring,
} from "./sticker-motion";
import { requestDeviceTiltAccess, useDeviceTilt } from "./use-device-tilt";
import { STICKER_REGISTRY, type WalletSticker } from "./wallet-stickers";

/**
 * The floating trading card a sticker is shown as — the wallet showcase, and
 * every place a sticker is shared (posts, comments, profiles). One card, one
 * pose loop, so a shared sticker looks exactly like it does in the wallet.
 */

/** A phone moving is the viewer looking round the card, not a hard turn. */
const DEVICE_TILT_SCALE = 0.75;
export const STICKER_CARD_ASPECT = 1.4;

const DUST =
  "radial-gradient(rgba(255,255,255,0.07) 1px, rgba(255,255,255,0) 1.4px)";

// The holographic foil stripe that sweeps the card as it turns.
const HOLO_SHEEN =
  "linear-gradient(105deg, rgba(255,255,255,0) 22%, rgba(255,190,235,0.16) 36%, rgba(255,255,255,0.32) 47%, rgba(170,220,255,0.2) 56%, rgba(190,255,230,0.12) 64%, rgba(255,255,255,0) 78%)";

const clampUnit = (value: number) => Math.max(-1, Math.min(1, value));

export interface StickerPoseValues {
  rotateX: MotionValue<number>;
  rotateY: MotionValue<number>;
  y: MotionValue<number>;
  blobY: MotionValue<number>;
  shadowX: MotionValue<number>;
  shadowScale: MotionValue<number>;
  shadowOpacity: MotionValue<number>;
  sheenX: MotionValue<string>;
  sheenOpacity: MotionValue<number>;
}

export interface StickerPoseInput {
  x: number;
  y: number;
  gyroX: number;
  gyroY: number;
  engaged: boolean;
}

/**
 * Motion budget:
 * - One `frame.update` loop drives every moving value (pose, shadow, sheen,
 *   glint, laminate). It runs only while `stageRef` is on screen and the page is
 *   visible, and never when `calm` (reduced motion or a weak device), where
 *   the card is posed once and nothing ticks.
 * - Everything that moves is a transform or an opacity over a pre-painted
 *   gradient; no filter, no layout read, no gradient string per frame.
 */
export function useStickerPose({
  stageRef,
  calm,
  blobSize,
  phase = 0,
}: {
  stageRef: RefObject<HTMLElement | null>;
  calm: boolean;
  blobSize: number;
  /**
   * Seconds into the idle drift this card starts at. Cards side by side get
   * different phases, so a row of them never sways in lockstep.
   */
  phase?: number;
}) {
  const blobSizeRef = useRef(blobSize);
  blobSizeRef.current = blobSize;

  const rotateX = useMotionValue(STATIC_POSE.pitch);
  const rotateY = useMotionValue(STATIC_POSE.yaw);
  const y = useMotionValue(0);
  const blobY = useMotionValue(0);
  const shadowX = useMotionValue(10);
  const shadowScale = useMotionValue(1);
  const shadowOpacity = useMotionValue(0.8);
  const sheenXRaw = useMotionValue(0);
  const sheenX = useTransform(sheenXRaw, (value) => `${value}%`);
  const sheenOpacity = useMotionValue(0.2);
  const glintX = useMotionValue(0);
  const glintY = useMotionValue(0);
  const film = useMotionValue(0.12);
  const filmX = useMotionValue(0);
  const live = useMemo<StickerBlobLive>(
    () => ({ glintX, glintY, film, filmX }),
    [glintX, glintY, film, filmX],
  );
  const pose = useMemo<StickerPoseValues>(
    () => ({
      rotateX,
      rotateY,
      y,
      blobY,
      shadowX,
      shadowScale,
      shadowOpacity,
      sheenX,
      sheenOpacity,
    }),
    [
      rotateX,
      rotateY,
      y,
      blobY,
      shadowX,
      shadowScale,
      shadowOpacity,
      sheenX,
      sheenOpacity,
    ],
  );

  const apply = useCallback(
    (layers: StickerLayers) => {
      const size = blobSizeRef.current;
      rotateX.set(layers.rotateX);
      rotateY.set(layers.rotateY);
      y.set(layers.y);
      blobY.set(layers.blobY);
      shadowX.set(layers.shadowX);
      shadowScale.set(layers.shadowScale);
      shadowOpacity.set(layers.shadowOpacity);
      sheenXRaw.set(layers.sheenX);
      sheenOpacity.set(layers.sheenOpacity);
      glintX.set(layers.glintX * size);
      glintY.set(layers.glintY * size);
      film.set(layers.film);
      filmX.set(layers.filmX);
    },
    [
      rotateX,
      rotateY,
      y,
      blobY,
      shadowX,
      shadowScale,
      shadowOpacity,
      sheenXRaw,
      sheenOpacity,
      glintX,
      glintY,
      film,
      filmX,
    ],
  );

  // Input: pointer/finger and the phone's own tilt, each -1..1.
  const inputRef = useRef<StickerPoseInput>({
    x: 0,
    y: 0,
    gyroX: 0,
    gyroY: 0,
    engaged: false,
  });
  const springXRef = useRef<SpringState>({ value: 0, velocity: 0 });
  const springYRef = useRef<SpringState>({ value: 0, velocity: 0 });

  const setDeviceTilt = useCallback((x: number, y: number) => {
    inputRef.current.gyroX = x * DEVICE_TILT_SCALE;
    inputRef.current.gyroY = y * DEVICE_TILT_SCALE;
  }, []);
  useDeviceTilt(stageRef, !calm, setDeviceTilt);

  useEffect(() => {
    const stage = stageRef.current;
    if (!stage) return;
    if (calm) {
      // Still, but not all at the same angle when several share a row.
      apply(
        phase
          ? layersForPose(idlePose(phase, 0.8), phase)
          : layersForPose(STATIC_POSE, 0),
      );
      return;
    }

    let visible = false;
    let running = false;
    let seconds = phase;
    let engage = 0;

    const tick = ({ delta }: FrameData) => {
      const dt = Math.min(delta / 1000, 1 / 20);
      seconds += dt;
      const input = inputRef.current;
      springXRef.current = stepSpring(
        springXRef.current,
        clampUnit(input.x + input.gyroX),
        dt,
      );
      springYRef.current = stepSpring(
        springYRef.current,
        clampUnit(input.y + input.gyroY),
        dt,
      );
      // Hands on the card quiet the idle drift instead of fighting it.
      engage = approach(engage, input.engaged ? 1 : 0, dt, 0.35);
      const idle = idlePose(seconds, 1 - engage * 0.75);
      apply(
        layersForPose(
          {
            yaw: idle.yaw + springXRef.current.value * INPUT_TILT.yawDeg,
            pitch: idle.pitch - springYRef.current.value * INPUT_TILT.pitchDeg,
            lift: idle.lift,
          },
          seconds,
        ),
      );
    };

    const sync = () => {
      const want = visible && !document.hidden;
      if (want === running) return;
      running = want;
      if (want) frame.update(tick, true);
      else cancelFrame(tick);
    };

    // `display: none` (a route parked by Activity) reports not intersecting,
    // so a hidden page never keeps the loop alive.
    const observer = new IntersectionObserver(([entry]) => {
      visible = Boolean(entry?.isIntersecting);
      sync();
    });
    observer.observe(stage);
    document.addEventListener("visibilitychange", sync);
    return () => {
      observer.disconnect();
      document.removeEventListener("visibilitychange", sync);
      cancelFrame(tick);
    };
  }, [apply, calm, phase, stageRef]);

  return { pose, live, inputRef, springXRef };
}

export function StickerCastShadow({
  width,
  cardHeight,
  pose,
}: {
  width: number;
  cardHeight: number;
  pose: StickerPoseValues;
}) {
  // A gradient ellipse, not a blurred box: it moves on the compositor with
  // no filter to re-run.
  return (
    <motion.div
      aria-hidden
      className="pointer-events-none absolute left-1/2 h-10 [--cast:rgba(36,22,70,0.34)] dark:[--cast:rgba(0,0,0,0.85)]"
      style={{
        width: width * 0.95,
        marginLeft: -(width * 0.95) / 2,
        top: `calc(50% + ${cardHeight / 2 - 4}px)`,
        background:
          "radial-gradient(closest-side, var(--cast), color-mix(in srgb, var(--cast) 45%, transparent) 55%, transparent)",
        x: pose.shadowX,
        scaleX: pose.shadowScale,
        opacity: pose.shadowOpacity,
      }}
    />
  );
}

export function StickerCardFace({
  sticker,
  locked,
  width,
  height,
  blobSize,
  subtitle = "collection",
  footerLeft,
  footerRight,
  compact = false,
  cardRef,
  live,
  pose,
}: {
  sticker: WalletSticker;
  locked: boolean;
  width: number;
  height: number;
  blobSize: number;
  /** Under "Hiive stickers": "collection", or whose it is. */
  subtitle?: string;
  footerLeft: string;
  footerRight: string;
  /** A feed-sized card: tighter type, no description. */
  compact?: boolean;
  cardRef?: (element: HTMLDivElement | null) => void;
  live: StickerBlobLive;
  pose: StickerPoseValues;
}) {
  const tone = stickerTone(sticker);
  const serial = stickerSerial(sticker, STICKER_REGISTRY);
  const titleGradient = locked
    ? "linear-gradient(100deg, #f4f4f5, #a1a1aa)"
    : `linear-gradient(100deg, ${tone.from}, color-mix(in oklab, ${tone.to} 72%, white))`;

  return (
    <motion.div
      ref={cardRef}
      className={cn(
        "relative [transform-style:preserve-3d]",
        compact ? "rounded-[16px]" : "rounded-[24px]",
      )}
      style={{
        width,
        height,
        rotateX: pose.rotateX,
        rotateY: pose.rotateY,
        y: pose.y,
      }}
    >
      {/* Body: flattened and clipped, so it can hold the sheen. */}
      <div
        className="absolute inset-0 overflow-hidden rounded-[inherit] bg-[#0b0b10]"
        style={{
          boxShadow:
            "inset 0 1px 0 rgba(255,255,255,0.14), inset 0 0 0 1px rgba(255,255,255,0.07)",
        }}
      >
        <div
          className="absolute inset-0"
          style={{
            background: `radial-gradient(75% 48% at 50% 40%, ${withAlpha(tone.to, locked ? 0.14 : 0.5)}, ${withAlpha(tone.to, 0)} 72%), radial-gradient(60% 35% at 22% 92%, ${withAlpha(tone.from, locked ? 0.05 : 0.16)}, ${withAlpha(tone.from, 0)} 70%)`,
          }}
        />
        <div
          className="absolute inset-0 opacity-80"
          style={{ backgroundImage: DUST, backgroundSize: "13px 13px" }}
        />
        <motion.div
          className="absolute inset-y-[-10%] left-[15%] w-[70%]"
          style={{
            background: HOLO_SHEEN,
            x: pose.sheenX,
            opacity: pose.sheenOpacity,
          }}
        />
      </div>

      <div
        className={cn(
          "absolute inset-x-0 top-0 flex items-start justify-between",
          compact ? "px-3 pt-2.5" : "px-5 pt-4",
        )}
        style={{ transform: "translateZ(16px)" }}
      >
        <div className="min-w-0 leading-tight">
          <p
            className={cn(
              "font-bold text-white/60 uppercase",
              compact
                ? "text-[8px] tracking-[0.18em]"
                : "text-[10px] tracking-[0.22em]",
            )}
          >
            {compact ? "Hiive" : "Hiive stickers"}
          </p>
          {compact ? null : (
            <p className="truncate text-[10px] tracking-[0.06em] text-white/35">
              {subtitle}
            </p>
          )}
        </div>
        <p
          className={cn(
            "shrink-0 font-semibold text-white/45 tabular-nums",
            compact
              ? "text-[8px] tracking-[0.12em]"
              : "text-[10px] tracking-[0.14em]",
          )}
        >
          No. {serial}
        </p>
      </div>

      <motion.div
        className="absolute inset-x-0 flex justify-center"
        style={{ top: height * 0.15, y: pose.blobY, z: 44 }}
      >
        <StickerBlob
          sticker={sticker}
          size={blobSize}
          locked={locked}
          live={locked ? undefined : live}
        />
        {locked ? (
          <span
            className="absolute top-1/2 left-1/2 flex size-11 items-center justify-center rounded-full border border-white/25 bg-white/15 text-white shadow-[inset_0_1px_0_rgba(255,255,255,0.45),0_8px_20px_-8px_rgba(0,0,0,0.6)]"
            style={{ transform: "translate(-50%, -50%) translateZ(18px)" }}
          >
            <Lock className="size-[18px]" strokeWidth={2.4} />
          </span>
        ) : null}
      </motion.div>

      <div
        className={cn(
          "absolute inset-x-0 bottom-0",
          compact ? "px-3 pb-2.5" : "px-5 pb-4",
        )}
        style={{ transform: "translateZ(24px)" }}
      >
        <h2
          className="bg-clip-text leading-[1.02] font-extrabold tracking-[-0.02em] text-transparent"
          style={{
            backgroundImage: titleGradient,
            WebkitBackgroundClip: "text",
            fontSize: Math.round(width * (compact ? 0.105 : 0.098)),
          }}
        >
          {sticker.title}
        </h2>
        {compact ? null : (
          <p className="mt-1.5 line-clamp-2 text-[12.5px] leading-snug text-white/60">
            {sticker.description}
          </p>
        )}
        <div
          className={cn(
            "flex items-center justify-between border-t border-white/10 font-semibold text-white/45 uppercase",
            compact
              ? "mt-1.5 pt-1.5 text-[8px] tracking-[0.1em]"
              : "mt-3 pt-2.5 text-[10px] tracking-[0.14em]",
          )}
        >
          <span className="truncate">{footerLeft}</span>
          <span className="shrink-0 pl-2 tabular-nums">{footerRight}</span>
        </div>
      </div>
    </motion.div>
  );
}

/**
 * One sticker card on its own: the shared-sticker embed in a post or comment,
 * a profile tile, the detail sheet. The card follows a mouse; with `drag` a
 * finger turns it too (only where the stage owns its touches — never inside a
 * scrolling feed, where a finger must still scroll the page).
 */
export function FloatingSticker({
  sticker,
  width,
  locked = false,
  subtitle,
  footerLeft = locked ? "Locked" : "Collected",
  footerRight,
  compact = width < 200,
  drag = false,
  phase,
  className,
}: {
  sticker: WalletSticker;
  width: number;
  locked?: boolean;
  subtitle?: string;
  footerLeft?: string;
  footerRight?: string;
  compact?: boolean;
  drag?: boolean;
  /** Idle-drift start, in seconds. Defaults to one derived from the sticker. */
  phase?: number;
  className?: string;
}) {
  const calm = useLowPowerMode();
  const stageRef = useRef<HTMLDivElement>(null);
  const cardRef = useRef<HTMLDivElement | null>(null);
  const setCardRef = useCallback((element: HTMLDivElement | null) => {
    cardRef.current = element;
  }, []);
  const height = Math.round(width * STICKER_CARD_ASPECT);
  const blobSize = Math.round(width * 0.6);
  const { pose, live, inputRef, springXRef } = useStickerPose({
    stageRef,
    calm,
    blobSize,
    // The laminate hash is already a stable per-sticker spread (0..359): map it
    // onto a 30s stretch of the drift.
    phase: phase ?? (stickerRimAngle(sticker.id) / 360) * 30,
  });
  const boxRef = useRef<{
    cx: number;
    cy: number;
    hw: number;
    hh: number;
  } | null>(null);
  const pressRef = useRef<{ id: number; x: number; t: number } | null>(null);

  const measure = () => {
    const card = cardRef.current;
    if (!card) return;
    const rect = card.getBoundingClientRect();
    boxRef.current = {
      cx: rect.left + rect.width / 2,
      cy: rect.top + rect.height / 2,
      hw: Math.max(1, rect.width / 2),
      hh: Math.max(1, rect.height / 2),
    };
  };
  const aim = (clientX: number, clientY: number) => {
    const box = boxRef.current;
    if (!box || calm) return;
    inputRef.current.engaged = true;
    inputRef.current.x = clampUnit((clientX - box.cx) / box.hw);
    inputRef.current.y = clampUnit((clientY - box.cy) / box.hh);
  };
  const release = () => {
    inputRef.current.engaged = false;
    inputRef.current.x = 0;
    inputRef.current.y = 0;
  };

  const onPointerDown = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (!drag || event.button !== 0) return;
    event.currentTarget.setPointerCapture(event.pointerId);
    measure();
    pressRef.current = {
      id: event.pointerId,
      x: event.clientX,
      t: event.timeStamp,
    };
    aim(event.clientX, event.clientY);
  };
  const onPointerMove = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (pressRef.current?.id === event.pointerId) {
      aim(event.clientX, event.clientY);
      return;
    }
    // Desktop hover: the card turns to look at the pointer.
    if (event.pointerType !== "mouse" || calm) return;
    if (!boxRef.current) measure();
    aim(event.clientX, event.clientY);
  };
  const onPointerUp = (event: ReactPointerEvent<HTMLDivElement>) => {
    const press = pressRef.current;
    if (!press || press.id !== event.pointerId) return;
    pressRef.current = null;
    if (event.pointerType !== "mouse") release();
    // A tap flicks the card round, like spinning a coin on a table.
    if (!calm && event.timeStamp - press.t < 350) {
      const dx = event.clientX - press.x;
      if (Math.abs(dx) < 8) {
        springXRef.current.velocity += dx >= 0 ? 7 : -7;
        uiHaptics.tap();
      }
    }
  };

  return (
    <div
      ref={stageRef}
      aria-hidden
      className={cn(
        "relative flex items-center justify-center [perspective:1100px]",
        drag && "touch-none select-none",
        className,
      )}
      style={{ width: width + 24, height: height + 32 }}
      data-vaul-no-drag={drag ? "" : undefined}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={() => {
        pressRef.current = null;
        release();
      }}
      onPointerEnter={(event) => {
        if (event.pointerType === "mouse") measure();
      }}
      onPointerLeave={(event) => {
        if (event.pointerType === "mouse" && !pressRef.current) release();
      }}
      onClick={requestDeviceTiltAccess}
    >
      <StickerCastShadow width={width} cardHeight={height} pose={pose} />
      <StickerCardFace
        sticker={sticker}
        locked={locked}
        width={width}
        height={height}
        blobSize={blobSize}
        subtitle={subtitle}
        footerLeft={footerLeft}
        footerRight={
          footerRight ??
          `${stickerSerial(sticker, STICKER_REGISTRY)}/${STICKER_REGISTRY.length}`
        }
        compact={compact}
        cardRef={setCardRef}
        live={live}
        pose={pose}
      />
    </div>
  );
}
